import React from 'react';

import cn from 'classnames';
import { bool } from 'prop-types';

import { useTranslation } from 'react-i18next';

import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons/faQuestionCircle';

import ButtonLink from 'components/Common/Buttons/ButtonLink';
import Flexbox from 'components/Common/Flexbox';
import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';
import Text from 'components/Common/Text';
import useCopyToClipboard from 'components/Common/TextCopy/useCopyToClipboard';
import Api from 'core/api';
import useFetch from 'hooks/useFetch';
import useMutation from 'hooks/useMutation';
import useUserRedux from 'hooks/useUserRedux';
import { ensureObject } from 'utils/objects';

import WalletVisibilitySwitch from 'modules/CredentialWallet/components/ListPageHeader/components/WalletVisibilitySwitch';

import s from './ListPageHeader.css';

const ListPageHeader = ({ isPublic }) => {
  const { t } = useTranslation();

  const publicLinkTooltip = t(
    'CredentialWallet.ListPageHeader.PublicLinkTooltip',
  );

  const { data: user } = useUserRedux();
  const { studentId } = ensureObject(user);

  const { copyToClipboard } = useCopyToClipboard({
    text: `${window.location.href}/${studentId}`,
  });

  const { data: profile } = useFetch({
    apiFunc: Api.studentProfile.getStudentProfile,
    apiFuncName: 'studentProfile.getStudentProfile',
    options: {
      enabled: !isPublic,
    },
  });
  const { isCredentialWalletPublic } = ensureObject(profile);

  const { mutateAsync: updateStudentProfile } = useMutation({
    apiFunc: Api.studentProfile.updateStudentProfile,
    apiFuncName: 'studentProfile.updateStudentProfile',
    invalidateQueries: ['studentProfile.getStudentProfile'],
  });

  return (
    <>
      <Flexbox
        justifyContent="space-between"
        width="100%"
        className={s.headerContainer}
      >
        <Flexbox width="25%" className={s.headerSection} />
        <Flexbox
          width="50%"
          alignItems="center"
          flexDirection="column"
          rowGap={1}
          className={s.headerSection}
        >
          <Text variant="h4">
            {t('CredentialWallet.ListPageHeader.CredentialWallet')}
          </Text>
          <Text variant="bodyMd" className={s.subtitle} align="center">
            {t('CredentialWallet.ListPageHeader.ADigitalRecordOfAcademicA')}
          </Text>
          <ButtonLink
            variant="text"
            label={t('CredentialWallet.ListPageHeader.LearnMore')}
            hasIconLeft
            icon={<FontAwesomeIcon icon={faQuestionCircle} />}
            href="https://support.schoolinks.com/en/articles/10671952-student-schoolinks-credential-wallet-guide"
          />
        </Flexbox>
        <Flexbox
          width="25%"
          alignItems="flex-end"
          flexDirection="column"
          rowGap={0.5}
          className={cn(s.headerSection, s.headerSectionRight)}
        >
          {!isPublic && (
            <WalletVisibilitySwitch
              isCredentialWalletPublic={isCredentialWalletPublic}
              updateStudentProfile={updateStudentProfile}
              publicLinkTooltip={publicLinkTooltip}
              copyToClipboard={copyToClipboard}
            />
          )}
        </Flexbox>
      </Flexbox>
    </>
  );
};

ListPageHeader.propTypes = {
  isPublic: bool,
};

ListPageHeader.defaultProps = {
  isPublic: false,
};

export default ListPageHeader;
